import BaseModule from '../_base_module';

const state = {
  ...BaseModule.state,

  data: [],
};

// getters
const getters = {
  ...BaseModule.getters,
  /**
   * The broadcast history, exactly as the server returned it.
   *
   * DELIBERATELY UNSORTED HERE. The server is the ordering authority —
   * `GET /v1/broadcasts` returns newest first BY ID through the model's own
   * `scopeNewestFirst()` (44-06/44-07) — and the page re-sorts defensively over
   * a COPY. Sorting in the store as well would make a server-side regression
   * silently correct in two places instead of visible in one.
   *
   * @returns {Array}
   */
  all(state, getters, rootState, rootGetters) {
    return state.data;
  },
  /**
   * Finds a loaded broadcast by id.
   *
   * STRICT on id type, on purpose, matching `news.js`. `f7route.params.*` is a
   * STRING and the resource's `id` is a NUMBER, so the CALLER coerces:
   *
   *   this.$store.getters['broadcasts/by_id'](parseInt(this.f7route.params.broadcast_id))
   *
   * A loose `==` here would spare the caller that line and would also silently
   * absorb a contract regression in BroadcastResource's `id` type. The
   * inherited BaseModule `find` getter does parseInt internally; this one
   * deliberately does not, so a type mismatch fails visibly rather than quietly.
   *
   * @returns {function(number): (Object|undefined)}
   */
  by_id(state, getters, rootState, rootGetters) {
    return (id) => state.data.find(item => item.id === id);
  },
};

// actions
const actions = {
  ...BaseModule.actions,
  /**
   * Fetches the whole broadcast history for the super-admin list (B2).
   *
   * CAST-07's compliance record. Unpaginated on both sides: 44-07 measured the
   * endpoint returning every row, and a client-side cap would hide a send from
   * the only screen that shows it.
   *
   * Deliberately NOT silent, exactly as `news/fetch_all_admin` is not. On this
   * screen a load failure leaves the operator with nothing to act on, so it
   * must surface. The page renders an inline failure block and adds no message
   * of its own, so the global handler's toast is the ONE message and the two
   * are not duplicates of each other.
   *
   * The mount, the retry button and the pull-to-refresh gesture (D-44-14) all
   * run through THIS action. One fetch path, because being the record is this
   * screen's whole job and a second path would be a second definition of it.
   *
   * Commits only in the `.then`, so a failed re-fetch leaves the rows the
   * operator is already reading in place rather than emptying the screen.
   *
   * @returns {Promise<any> | Thenable<any> | PromiseLike<any> | *}
   */
  fetch_all ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/getEndpoint', {
      endpoint: '/broadcasts',
    }, {root: true}).then(response => {
      commit('set_state', {property: 'data', value: response.data.data});
    });
  },

  /**
   * The four audience identifiers with the SERVER's own description of each set
   * (IC3, CD-03).
   *
   * THE SPLIT IS THE POINT. The composer owns the four NAMES, because they are a
   * four-item allowlist that must be pinned against the server enum by a test --
   * exactly as `CATEGORIES` in `src/js/push.js` is pinned today. The server owns
   * the DESCRIPTION, because CAST-02's "active member" was undefined in the
   * requirements until D-44-09 closed it, and a client sentence that drifts from
   * the predicate misleads an operator about a send they cannot recall.
   *
   * RESOLVES WITH THE ROWS AND COMMITS NOTHING. `data` on this module is the
   * compliance record B2 renders; four audience rows written into it would
   * appear on the history screen. It is also what keeps IC1 honest -- nothing
   * about the audience survives the page, so a second visit cannot inherit the
   * first visit's choice from the store.
   *
   * ===========================================================================
   *  THE TOAST IS SUPPRESSED HERE, AND THIS IS THE OPPOSITE OF `fetch_all`.
   * ===========================================================================
   * `fetch_all` is deliberately NOT silent: on the history screen a load failure
   * leaves the operator with nothing to act on, so it must surface. This call is
   * the other case. A failed description fetch is NOT a page failure -- the four
   * identifiers the server needs are client-side, so the composer still works
   * and the operator can still choose. B4's `error` row in 44-UI-SPEC.md says so
   * outright: four named rows, no second line, `No toast, no block.`
   *
   * That is `news/fetch_all`'s precedent exactly -- the supplementary panel that
   * must not put a red bar over a working reservations page -- and it is one of
   * the two call sites `api.js:135-155` names for this flag. The reason is
   * written HERE because that comment requires a reason at the call site.
   *
   * NOTE FOR 44-10, WHICH OWNS THE SEND ACTION: its acceptance criteria say the
   * flag appears `only in the send action`. That was written before this call
   * site existed, and it now has to read `in the send and audiences actions`.
   * The rule the criterion protects -- no general quiet mode, every use
   * deliberate and justified in place -- is unchanged, and the LIST action still
   * carries no flag.
   *
   * The flag lives on the payload ROOT and never inside `data` (T-43-32): a
   * flag inside `data` is appended to every GET as a query string.
   *
   * @returns {Promise<Array>} `[{ audience, label, description }]`, never a count
   */
  fetch_audiences ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/getEndpoint', {
      endpoint: '/broadcasts/audiences',
      suppress_error_toast: true,
    }, {root: true}).then(response => response.data.data);
  },

  /**
   * The broadcasts for ONE news item, newest first (B14, NEWS-06, D-44-07).
   *
   * The news composer's push block has one question to answer -- "did I already
   * push this?" -- and NEWS-06's separation is what creates it. Publishing
   * notifies nobody, so an author who saved an article has no way to know
   * whether anyone was told except by being told here.
   *
   * ===========================================================================
   *  A SEPARATE SUPER-ADMIN-GATED READ, AND NOT A WIDER `NewsResource`.
   * ===========================================================================
   * `NewsResource` is shared: `NewsController::index()` serves it to EVERY
   * member on `GET /news`. A `last_broadcast` key there would put operational
   * data -- who was reached, when, and whether the send failed -- into a
   * member-facing payload. So this rides `GET /v1/broadcasts?news_id={id}`,
   * which sits inside the `super_admin` group (44-07, routes/api.php:278-279),
   * and no member-facing shape changes at all (T-44-56).
   *
   * RESOLVES WITH THE ROWS AND COMMITS NOTHING, exactly as `fetch_audiences`
   * does and for the same reason: `data` on this module is the compliance record
   * B2 renders, and a news-filtered subset written into it would REPLACE the
   * history screen's rows with four of them.
   *
   * ===========================================================================
   *  THE TOAST IS SUPPRESSED HERE, AND THIS IS THE THIRD CALL SITE.
   * ===========================================================================
   * 44-UI-SPEC.md's B14 `error` row: if the last-broadcast data is missing the
   * block renders the NEVER-PUSHED body rather than an error. That is the safe
   * direction -- it invites a send the count gate then governs, instead of
   * hiding the control behind a failure -- and a red bar over a working news
   * composer would be a message about a supplementary read the author did not
   * ask for. Same precedent as `fetch_audiences` and as `news/fetch_all`.
   *
   * `fetch_all` still does NOT suppress: on the history screen a load failure
   * leaves the operator with nothing to act on. `api.js:143-146` refuses a
   * general quiet mode by name, so this is written here rather than assumed.
   *
   * The flag lives on the payload ROOT and never inside `data` (T-43-32):
   * `payload.data` is assigned to axios's `params` for a GET, so a flag placed
   * there would be appended to this request as a query string.
   *
   * @param {{news_id: number}} payload
   * @returns {Promise<Array>} the rows for that item, newest first
   */
  fetch_for_news ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/getEndpoint', {
      endpoint: '/broadcasts',
      data: {news_id: payload.news_id},
      suppress_error_toast: true,
    }, {root: true}).then(response => response.data.data);
  },

  /**
   * The FIRST half of IC7's two-phase send: resolve, count, hand back a token.
   * Nothing is sent by this call.
   *
   * D-44-02 makes the count come from the same server-side resolution that
   * performs the send, so this action must never compute, cache or default one.
   * It posts the draft and returns exactly what the server answered.
   *
   * A FLAT BODY, not the `data` envelope the resource endpoints use:
   * `{ audience_size, recipient_count, confirmation_token }` (44-07). The token
   * is not a column anywhere and exists only in that response.
   *
   * NO `suppress_error_toast` HERE. IC8 puts that flag on the SEND call alone,
   * where B12 authors all three bodies itself. Widening it to this read is
   * exactly the general "quiet mode" `api.js:143-146` refuses by name.
   *
   * @param {{audience: string, member_id: ?number, category: string, title: string, body: string}} payload
   * @returns {Promise<{audience_size: number, recipient_count: number, confirmation_token: string}>}
   */
  confirm ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/postEndpoint', {
      endpoint: '/broadcasts/confirm',
      data: payload,
    }, {root: true}).then(response => response.data);
  },

  /**
   * The SECOND half of IC7's two-phase send. THIS IS THE IRREVERSIBLE CALL.
   *
   * It carries the confirmation token and nothing else. The server re-resolves
   * the audience from the stored row, sends, and records the delta -- so the
   * draft is deliberately NOT re-posted here. A second copy of the draft on this
   * call would be a second definition of what was sent.
   *
   * A FLAT BODY comes back, not the `data` envelope the resource endpoints use:
   * `{ sent_recipient_count, confirmed_recipient_count }` (44-01). Both numbers
   * are returned together on purpose, and the composer renders BOTH when they
   * differ -- that is D-44-02's gap closed by telling the operator rather than
   * by pretending the set cannot move between the two calls.
   *
   * ===========================================================================
   *  THE TOAST IS SUPPRESSED HERE, AND THIS IS THE THIRD AND LAST CALL SITE.
   * ===========================================================================
   * IC8: a failed send gets EXACTLY ONE message, and B12 on the composer is it.
   * B12 is persistent, authors its own Dutch for the rate-limited and generic
   * cases, and converts `Retry-After` into minutes -- none of which the global
   * handler can do, because it renders `error.response.data.message` verbatim
   * into a toast that fades in five seconds (`api.js:239-247`). A failed send
   * leaves the operator with a draft they must change and re-send, and a message
   * that vanishes is a message they will re-read by pressing send again.
   *
   * THE OTHER TWO CALL SITES IN THIS MODULE ARE DELIBERATELY DIFFERENT, and the
   * asymmetry is the whole point of the flag:
   *   - `fetch_audiences` suppresses it, because B4's `error` row says `No toast,
   *     no block.` -- see its own docblock.
   *   - `confirm` does NOT. Its 422 carries the form request's own field bag,
   *     which names the offending field, and nothing this client could author
   *     would beat that.
   *   - `fetch_all` does NOT. On the history screen a load failure leaves the
   *     operator with nothing to act on, so it must surface.
   * `api.js:143-146` refuses a general quiet mode by name. Every use is
   * deliberate and justified where it is written.
   *
   * The flag lives on the payload ROOT and never inside `data` (T-43-32):
   * `payload.data` is assigned straight to axios's `data` for a write and to
   * `params` for a read, so a flag placed there would be serialised into this
   * POST body and appended as a query string to every GET.
   *
   * @param {{confirmation_token: string}} payload
   * @returns {Promise<{sent_recipient_count: number, confirmed_recipient_count: number}>}
   */
  send ({state, commit, dispatch, getters, rootState}, payload) {
    return dispatch('api/postEndpoint', {
      endpoint: '/broadcasts/send',
      data: payload,
      suppress_error_toast: true,
    }, {root: true}).then(response => response.data);
  },
};

// NO `.catch` ON ANY ACTION ABOVE, and that is deliberate rather than an
// oversight. `api/onError` (api.js) already renders the server's form-request
// error bag, which names the offending field — nothing the client could author
// would beat that, so a second client-side message would be a duplicate. This
// is 43-UI-SPEC.md's one-message rule, inherited verbatim from news.js.
//
// A `.catch` here would also do a second, worse thing: it would RESOLVE the
// promise. The history page sets its own error flag in its own `.catch`, so a
// swallowed rejection would leave that page on its preloader for ever.
// `tests/store/broadcasts.test.js` asserts the rejection rather than the
// comment, because a comment cannot fail.

// mutations
const mutations = {
  ...BaseModule.mutations,
};

export default {

  namespaced: true,

  state,
  getters,
  actions,
  mutations
};
